import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { X, Package } from 'lucide-react';

interface AdjustInventoryModalProps {
  supply: {
    supply_id: string;
    item_name: string;
    quantity_on_hand: number;
  };
  isOpen: boolean;
  onClose: () => void;
}

interface TransactionFormData {
  transaction_type: string;
  quantity_change: number;
  lot_number: string;
  reference_type: string;
  reference_id: string;
  notes: string;
}

export default function AdjustInventoryModal({ supply, isOpen, onClose }: AdjustInventoryModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<TransactionFormData>({
    transaction_type: 'Received',
    quantity_change: 0,
    lot_number: '',
    reference_type: 'manual',
    reference_id: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const adjustMutation = useMutation({
    mutationFn: async (data: TransactionFormData) => {
      // Calculate quantity_after
      const quantity_after = supply.quantity_on_hand + data.quantity_change;

      if (quantity_after < 0) {
        throw new Error('Cannot reduce quantity below zero');
      }

      // Insert transaction
      const { error: txnError } = await supabase
        .from('or_supply_transactions')
        .insert({
          supply_id: supply.supply_id,
          transaction_type: data.transaction_type,
          quantity_change: data.quantity_change,
          quantity_after: quantity_after,
          lot_number: data.lot_number || null,
          reference_type: data.reference_type,
          reference_id: data.reference_id || null,
          notes: data.notes || null,
        });

      if (txnError) throw txnError;

      // Update supply quantity
      const { error: updateError } = await supabase
        .from('or_surgical_supplies')
        .update({ quantity_on_hand: quantity_after })
        .eq('supply_id', supply.supply_id);

      if (updateError) throw updateError;

      return { quantity_after };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply', supply.supply_id] });
      queryClient.invalidateQueries({ queryKey: ['supply-transactions', supply.supply_id] });
      queryClient.invalidateQueries({ queryKey: ['supplies'] });
      handleClose();
    },
    onError: (error: any) => {
      alert(`Error adjusting inventory: ${error.message}`);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.quantity_change === 0) {
      newErrors.quantity_change = 'Quantity change cannot be zero';
    }

    if (formData.transaction_type === 'Used' && formData.quantity_change > 0) {
      newErrors.quantity_change = 'Used transactions should have negative quantity';
    }

    if (formData.transaction_type === 'Received' && formData.quantity_change < 0) {
      newErrors.quantity_change = 'Received transactions should have positive quantity';
    }

    const quantity_after = supply.quantity_on_hand + formData.quantity_change;
    if (quantity_after < 0) {
      newErrors.quantity_change = `Cannot reduce quantity below zero (current: ${supply.quantity_on_hand})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      adjustMutation.mutate(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      transaction_type: 'Received',
      quantity_change: 0,
      lot_number: '',
      reference_type: 'manual',
      reference_id: '',
      notes: '',
    });
    setErrors({});
    onClose();
  };

  const handleTransactionTypeChange = (type: string) => {
    setFormData({ 
      ...formData, 
      transaction_type: type,
      // Auto-flip sign based on transaction type
      quantity_change: Math.abs(formData.quantity_change) * (type === 'Used' ? -1 : 1)
    });
  };

  const handleChange = (field: keyof TransactionFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  if (!isOpen) return null;

  const quantity_after = supply.quantity_on_hand + formData.quantity_change;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-pt-surface border border-pt-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-pt-border">
          <div>
            <h2 className="text-2xl font-bold text-pt-gold">Adjust Inventory</h2>
            <p className="text-sm text-pt-text-muted mt-1">{supply.item_name}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-pt-text-muted hover:text-pt-gold transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Current Quantity Display */}
          <div className="bg-pt-bg border border-pt-border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-pt-text-muted uppercase tracking-wider mb-1">
                  Current Quantity
                </div>
                <div className="text-3xl font-bold text-pt-text">{supply.quantity_on_hand}</div>
              </div>
              <div className="text-2xl text-pt-text-muted">→</div>
              <div>
                <div className="text-xs text-pt-text-muted uppercase tracking-wider mb-1">
                  After Transaction
                </div>
                <div className={`text-3xl font-bold ${quantity_after < 0 ? 'text-red-400' : 'text-pt-gold'}`}>
                  {quantity_after}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Transaction Type */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Transaction Type <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['Received', 'Used', 'Adjusted'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTransactionTypeChange(type)}
                    className={`px-4 py-2 rounded border transition-colors ${
                      formData.transaction_type === type
                        ? 'bg-pt-gold text-pt-bg border-pt-gold'
                        : 'bg-pt-bg border-pt-border text-pt-text hover:border-pt-gold'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['Expired', 'Returned', 'Disposed'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTransactionTypeChange(type)}
                    className={`px-4 py-2 rounded border transition-colors ${
                      formData.transaction_type === type
                        ? 'bg-pt-gold text-pt-bg border-pt-gold'
                        : 'bg-pt-bg border-pt-border text-pt-text hover:border-pt-gold'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity Change */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Quantity Change <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={formData.quantity_change}
                onChange={(e) => handleChange('quantity_change', parseInt(e.target.value) || 0)}
                className={`w-full bg-pt-bg border ${errors.quantity_change ? 'border-red-500' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                placeholder="Enter positive for increase, negative for decrease"
              />
              {errors.quantity_change && (
                <p className="text-red-400 text-xs mt-1">{errors.quantity_change}</p>
              )}
              <p className="text-xs text-pt-text-muted mt-1">
                Tip: Use negative numbers for Used/Expired/Disposed transactions
              </p>
            </div>

            {/* Lot Number */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Lot Number
              </label>
              <input
                type="text"
                value={formData.lot_number}
                onChange={(e) => handleChange('lot_number', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text font-mono focus:outline-none focus:border-pt-gold"
                placeholder="e.g., LOT2024-001"
              />
            </div>

            {/* Reference Type */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Reference Type
              </label>
              <select
                value={formData.reference_type}
                onChange={(e) => handleChange('reference_type', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
              >
                <option value="manual">Manual Adjustment</option>
                <option value="case_log">Case Log</option>
                <option value="purchase_order">Purchase Order</option>
              </select>
            </div>

            {/* Reference ID */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Reference ID
              </label>
              <input
                type="text"
                value={formData.reference_id}
                onChange={(e) => handleChange('reference_id', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text font-mono focus:outline-none focus:border-pt-gold"
                placeholder="e.g., PO-2024-001 or Case #12345"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
                rows={3}
                placeholder="Additional notes about this transaction..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 bg-pt-bg border border-pt-border rounded text-pt-text hover:border-red-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={adjustMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-pt-gold text-pt-bg rounded hover:bg-pt-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Package className="w-4 h-4" />
              {adjustMutation.isPending ? 'Recording...' : 'Record Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
